import { sweetAlert } from "../../../../../utils/UniversalFunction";

const SUPPLIER_INVITATION_ACCEPT_PATH = "/supplier-invitation/accept";

export function normaliseRow(row) {
  const supplier = row?.supplier || null;
  const profile = supplier?.profile || null;

  const supplierId = row?.id ?? supplier?.id;
  const companyName =
    row?.register_company_name ??
    supplier?.company_name ??
    "";
  const firstName = row?.first_name ?? supplier?.first_name ?? "";
  const lastName = row?.last_name ?? supplier?.last_name ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "";
  const email = row?.email ?? supplier?.email ?? "";
  const mobile = row?.mobile_number ?? supplier?.mobile_number ?? "";
  const country = row?.country ?? profile?.country ?? "";
  const industryName = row?.industry_name ?? profile?.industry_name ?? "";
  const subIndustryName =
    row?.sub_industry_name ?? supplier?.sub_industry_name ?? "";

  return {
    mappingId: row?.mapping_id,
    supplierId,
    companyId: row?.company_id,
    status: row?.status,
    onboardingStatus: row?.onboarding_status,
    approvalStatus: row?.approval_status,
    companyName,
    industryName,
    subIndustryName,
    employeeSizeCategory:
      row?.employee_size_category ?? profile?.employee_size_category ?? "",
    entityType: row?.entity_type ?? profile?.entity_type ?? "",
    address: row?.registered_address ?? profile?.registered_address ?? "",
    mobile,
    email,
    contactName: fullName,
    country,
    profile,
    raw: row,
  };
}

export function normaliseInvitationRow(row) {
  return {
    id: row?.id,
    supplierId: row?.supplier_id,
    companyName:
      row?.invited_company_name ??
      row?.company_name ??
      row?.companyName ??
      "",
    email: row?.invited_email ?? row?.email ?? "",
    contactName: row?.invited_contact_name ?? "",
    phone: row?.invited_phone ?? "",
    status: row?.status,
    statusKey: row?.status_key ?? row?.statusKey ?? "",
    invitationStatus: row?.invitation_status,
    createdAt: row?.created_at,
    expiresAt: row?.expires_at,
    acceptedAt: row?.accepted_at,
    acceptedBy: row?.accepted_by,
    invitedBy: row?.invited_by,
    invitationToken:
      row?.invitation_token ?? row?.invitationToken ?? "",
    raw: row,
  };
}

export function extractInvitationList(apiPayload) {
  if (!apiPayload) return [];
  const nested = apiPayload.data;
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(apiPayload)) return apiPayload;
  if (nested && Array.isArray(nested.data)) return nested.data;
  if (Array.isArray(apiPayload.invitations)) return apiPayload.invitations;
  return [];
}

export function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function isInvitationExpired(expiresAt) {
  if (!expiresAt) return false;
  const d = new Date(expiresAt);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

export function isPendingInvitation(statusKey) {
  const key = (statusKey || "").toUpperCase();
  return key === "PENDING" || key === "";
}

export function buildSupplierInvitationAcceptUrl(invitationToken) {
  const token = (invitationToken || "").trim();
  if (!token) return null;
  if (typeof window === "undefined") return null;
  const base = `${window.location.origin}${window.location.pathname}`.replace(
    /\/$/,
    ""
  );
  return `${base}#${SUPPLIER_INVITATION_ACCEPT_PATH}/${encodeURIComponent(token)}`;
}

export async function copySupplierInvitationLink(invitationToken) {
  const url = buildSupplierInvitationAcceptUrl(invitationToken);
  if (!url) {
    sweetAlert("error", "Invitation link is not available.");
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    sweetAlert("success", "Invitation link copied to clipboard.");
  } catch {
    sweetAlert("error", "Could not copy link. Please copy it manually.");
  }
}

export function invitationStatusStyle(statusKey) {
  const key = (statusKey || "").toUpperCase();
  if (key === "PENDING") {
    return { bg: "#fef9c3", color: "#a16207", border: "#fde047" };
  }
  if (key === "ACCEPTED" || key === "ACTIVE") {
    return { bg: "#dcfce7", color: "#15803d", border: "#86efac" };
  }
  if (key === "REJECTED" || key === "DECLINED" || key === "CANCELLED") {
    return { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" };
  }
  return { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" };
}

export function invitationSearchText(row) {
  return [
    row.companyName,
    row.email,
    row.contactName,
    row.phone,
    row.statusKey,
    row.id,
    row.supplierId,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function supplierSearchText(row) {
  return [
    row.companyName,
    row.industryName,
    row.subIndustryName,
    row.address,
    row.contactName,
    row.email,
    row.mobile,
    row.entityType,
    row.employeeSizeCategory,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function describeLookupMatch(match) {
  if (!match) return null;
  const supplierLabel = match.company_name || "this supplier";
  if (match.matched_by === "email") {
    return {
      tone: "block",
      title: "This email is already registered",
      body: `${supplierLabel} already has a contact with this address. Pick a different email or open the supplier from the registered list.`,
    };
  }
  if (match.has_pending_invitation) {
    return {
      tone: "warn",
      title: `You already have a pending invitation for ${supplierLabel}`,
      body: "Resend it from the Invitations tab instead of creating a new one.",
    };
  }
  return {
    tone: "info",
    title: `${supplierLabel} is already registered`,
    body: `Sending this invitation will reuse the existing supplier profile (matched by ${match.matched_by || "lookup"}).`,
  };
}

export function getInvitationActionMenuItems(row, handlers) {
  const { openInvitationDetails, openResend, copyLink } = handlers;
  const items = [
    {
      key: "view",
      label: "View details",
      icon: "search",
      onClick: () => openInvitationDetails(row),
    },
  ];

  if (row.invitationToken && isPendingInvitation(row.statusKey)) {
    const inviteUrl = buildSupplierInvitationAcceptUrl(row.invitationToken);
    items.push({
      key: "copy-link",
      label: "Copy invitation link",
      icon: "export",
      onClick: () => copyLink(row.invitationToken),
    });
    if (inviteUrl) {
      items.push({
        key: "open-link",
        label: "Open invitation link",
        icon: "location",
        onClick: () => window.open(inviteUrl, "_blank", "noopener,noreferrer"),
      });
    }
  }

  if (row.supplierId) {
    items.push({
      key: "resend",
      label: "Resend invitation",
      icon: "upload",
      onClick: () => openResend(row.supplierId),
    });
  }

  return items;
}
